import React from 'react'
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react'
import { Navigation, FreeMode } from 'swiper/modules'
import { useSearchParams } from 'data/hooks/useSearchParams'
import { useHasLoaded } from 'data/hooks/useHasLoaded'
import classNames from 'classnames'
import { RestProperty } from 'data/types/property'
import PropertyCard from 'components/property/PropertyCard'

interface CarouselSimilarPropertiesProps {
  similarProperties: RestProperty[]
}

const CarouselSimilarProperties = ({
  similarProperties,
}: CarouselSimilarPropertiesProps) => {
  const { ref: componentRef, hasLoaded } = useHasLoaded()
  const { params } = useSearchParams()

  const navigationPrevRef = React.useRef(null)
  const navigationNextRef = React.useRef(null)
  const buttonClasses = 'visible !w-40 !h-40 sm:!w-50 sm:!h-50 top-1/2 -mt-20 sm:-mt-25 !hidden lg:!flex'

  return (
    <div ref={componentRef}>
      {
        hasLoaded && (
          <>
            <div className="relative flex items-center min-w-0 gap-x-20 lg:gap-x-30 2xl:gap-x-50">
              {
                similarProperties.length > 3 && (
                  <div ref={navigationPrevRef} className={classNames('swiper-button-prev-static ml-20 2xl:ml-0', buttonClasses)} />
                )
              }

              <Swiper
                modules={[Navigation, FreeMode]}
                slidesPerView={'auto'}
                navigation={{
                  prevEl: navigationPrevRef.current,
                  nextEl: navigationNextRef.current,
                }}
                spaceBetween={20}
                slidesOffsetAfter={20}
                slidesOffsetBefore={20}
                freeMode={{
                  enabled: true,
                  sticky: true,
                  momentum: true,
                  momentumVelocityRatio: 0.25,
                }}
                onSwiper={(swiper: SwiperClass) => {
                  setTimeout(() => {
                    if (swiper.params.navigation && typeof(swiper.params.navigation) !== 'boolean') {
                      // Timeout until prevEl & nextEl refs are defined
                      swiper.params.navigation.prevEl = navigationPrevRef.current
                      swiper.params.navigation.nextEl = navigationNextRef.current
                    }

                    // Re-initialise navigation
                    swiper.navigation.destroy()
                    swiper.navigation.init()
                    swiper.navigation.update()
                  })
                }}
              >
                {
                  similarProperties.map(property => (
                    <SwiperSlide
                      key={property.id}
                      className="max-w-[260px] md:max-w-[390px] w-full"
                    >
                      <PropertyCard
                        key={`property-${property.id}`}
                        property={property as any}
                        mainImageUrlOverride={property.main_image}
                        url={{
                          pathname: `/${property.url}`,
                          query: {
                            adults: params.get('adults'),
                            children: params.get('children'),
                            arrival: params.get('arrival'),
                            departure: params.get('departure'),
                          },
                        }}
                      />
                    </SwiperSlide>
                  ))}
              </Swiper>

              {
                similarProperties.length > 3 && (
                  <div ref={navigationNextRef} className={classNames('swiper-button-next-static mr-20 2xl:mr-0', buttonClasses)} />
                )
              }
            </div>

          </>
        )
      }
    </div>
  )
}

export default CarouselSimilarProperties
